/**
 * Presets baseline para el Particle Editor.
 *
 * Cada preset es una def v2 coherente que sirve como punto de partida.
 * Al hacer click en un botón de preset, el editor carga la def en draft
 * (sin guardar). El usuario tunea, asigna un fxId definitivo y guarda.
 *
 * NO tienen `id` definitivo — se asigna en el momento de guardar.
 * Frame indices son del atlas particles1 (ver PARTICLE_FRAME_NAMES en
 * ParticleSystem.ts).
 */

import type { ParticleEffectDefV2 } from '@shared/types/particles';

export interface PresetEntry {
  label: string;
  description: string;
  /** Sugerencia de id (el editor permite renombrar). */
  suggestedId: string;
  factory: () => ParticleEffectDefV2;
}

// ──────────────────────────────────────────────────────────────────────────
// Atlas único actual: 'particles1' (particles/1.png)
// Frame indices conocidos (parcial):
//   circle1=0..4, magic1=20..24, fire1=8..9, flame1=10..15, spark1=46..52,
//   smoke1=38..47, star1=53..61, twirl1=70..72, trace1=63..69, light1=17..19
// ──────────────────────────────────────────────────────────────────────────

const ATLAS = 'particles1';

function baseDef(overrides: Partial<ParticleEffectDefV2>): ParticleEffectDefV2 {
  return {
    id: 'fx_draft',
    version: 2,
    spriteKey: ATLAS,
    frames: [0],
    emission: { rate: 10, maxAlive: 30, life: [800, 1400] },
    blendMode: 'ADD',
    tint: [0xffffff],
    loop: true,
    duration: 0,
    ...overrides,
  };
}

// ── Auras ─────────────────────────────────────────────────────────────────

const auraHealing: PresetEntry = {
  label: 'Aura curativa',
  description: 'Círculos verdes orbitando alrededor del personaje',
  suggestedId: 'fx_heal_aura_loop',
  factory: () =>
    baseDef({
      tags: ['aura', 'heal'],
      frames: [0, 1, 17, 18],
      emission: { rate: 6, maxAlive: 12, life: [1000, 1500] },
      shape: { type: 'circle', radius: 24, edgeOnly: true },
      orbit: { radiusX: 28, radiusY: 14, speed: 90 },
      alphaOverLife: { stops: [[0, 0], [0.3, 0.9], [1, 0]] },
      sizeOverLife: { stops: [[0, 0.4], [1, 0.8]] },
      tint: [0x66ff88, 0xaaffcc, 0x44dd66],
    }),
};

const auraPoison: PresetEntry = {
  label: 'Aura veneno',
  description: 'Smoke verde tóxico subiendo',
  suggestedId: 'fx_poison_aura_loop',
  factory: () =>
    baseDef({
      tags: ['aura', 'debuff', 'dot'],
      frames: [38, 39, 40, 41],
      emission: { rate: 8, maxAlive: 20, life: [1200, 2000] },
      shape: { type: 'circle', radius: 16 },
      velocity: { mode: 'directional', angle: [-100, -80], speed: [10, 25] },
      alphaOverLife: { stops: [[0, 0], [0.2, 0.6], [1, 0]] },
      sizeOverLife: { stops: [[0, 0.5], [1, 1.4]] },
      tint: [0x66aa44, 0x99cc55, 0x447733],
      blendMode: 'NORMAL',
    }),
};

const auraShield: PresetEntry = {
  label: 'Aura escudo',
  description: 'Anillo de runas defensivas',
  suggestedId: 'fx_shield_aura_loop',
  factory: () =>
    baseDef({
      tags: ['aura', 'defensive'],
      frames: [62, 63],
      emission: { rate: 4, maxAlive: 8, life: [800, 1200] },
      shape: { type: 'circle', radius: 30, edgeOnly: true },
      orbit: { radiusX: 30, radiusY: 15, speed: 60 },
      alphaOverLife: { stops: [[0, 0], [0.5, 0.7], [1, 0]] },
      sizeOverLife: { stops: [[0, 0.6], [1, 0.4]] },
      tint: [0x66aaff, 0xaaccff],
    }),
};

// ── Proyectiles (anchor = projectile, viajan del caster al target) ────────

const projectileArcane: PresetEntry = {
  label: 'Proyectil arcano',
  description: 'Bolt morado con trail de chispas',
  suggestedId: 'fx_arcane_bolt_projectile',
  factory: () =>
    baseDef({
      tags: ['projectile', 'magical'],
      frames: [20, 21, 22],
      emission: { rate: 30, maxAlive: 30, life: [200, 400] },
      shape: { type: 'point' },
      velocity: { mode: 'random_rect', vx: [-15, 15], vy: [-15, 15] },
      alphaOverLife: { stops: [[0, 1], [1, 0]] },
      sizeOverLife: { stops: [[0, 1], [1, 0.2]] },
      tint: [0xaa66ff, 0xcc99ff],
      duration: 0,
    }),
};

const projectileFire: PresetEntry = {
  label: 'Proyectil fuego',
  description: 'Fireball con trail de fuego y smoke',
  suggestedId: 'fx_fireball_projectile',
  factory: () =>
    baseDef({
      tags: ['projectile', 'physical'],
      frames: [10, 11, 12],
      emission: { rate: 40, maxAlive: 40, life: [300, 500] },
      shape: { type: 'circle', radius: 4 },
      velocity: { mode: 'random_rect', vx: [-20, 20], vy: [-30, -10] },
      physics: { gravityY: 80 },
      alphaOverLife: { stops: [[0, 1], [1, 0]] },
      sizeOverLife: { stops: [[0, 1.2], [1, 0.4]] },
      tint: [0xff8833, 0xffcc44, 0xff5522],
    }),
};

const projectileIce: PresetEntry = {
  label: 'Proyectil hielo',
  description: 'Shards de hielo con trail cristalino',
  suggestedId: 'fx_ice_shard_projectile',
  factory: () =>
    baseDef({
      tags: ['projectile', 'magical'],
      frames: [53, 54, 55],
      emission: { rate: 25, maxAlive: 25, life: [250, 450] },
      shape: { type: 'point' },
      velocity: { mode: 'random_rect', vx: [-10, 10], vy: [-15, 5] },
      rotation: { spin: [-180, 180] },
      alphaOverLife: { stops: [[0, 0.9], [1, 0]] },
      sizeOverLife: { stops: [[0, 0.8], [1, 0.3]] },
      tint: [0x99ddff, 0xccf0ff, 0x66bbee],
    }),
};

// ── Impactos (burst one-shot) ─────────────────────────────────────────────

const impactBurst: PresetEntry = {
  label: 'Impacto burst genérico',
  description: 'Explosión radial de chispas blancas',
  suggestedId: 'fx_generic_impact_burst',
  factory: () =>
    baseDef({
      tags: ['impact'],
      frames: [46, 47, 48, 49, 50],
      emission: { rate: 0, burstCount: 25, maxAlive: 25, life: [300, 600] },
      shape: { type: 'point' },
      velocity: { mode: 'radial', speed: [80, 160] },
      physics: { drag: 0.6 },
      alphaOverLife: { stops: [[0, 1], [1, 0]] },
      sizeOverLife: { stops: [[0, 1.2], [1, 0.2]] },
      tint: [0xffffff, 0xffeeaa],
      loop: false,
      duration: 600,
    }),
};

const impactSparks: PresetEntry = {
  label: 'Impacto chispas',
  description: 'Chispas naranjas como metal golpeado',
  suggestedId: 'fx_metal_spark_impact',
  factory: () =>
    baseDef({
      tags: ['impact', 'physical'],
      frames: [46, 47],
      emission: { rate: 0, burstCount: 15, maxAlive: 15, life: [400, 800] },
      shape: { type: 'point' },
      velocity: { mode: 'radial', speed: [60, 140] },
      physics: { gravityY: 200, drag: 0.3 },
      rotation: { spin: [-360, 360] },
      alphaOverLife: { stops: [[0, 1], [0.8, 0.8], [1, 0]] },
      tint: [0xffaa44, 0xff7722],
      loop: false,
      duration: 800,
    }),
};

// ── Drops (sobre item en el piso) ─────────────────────────────────────────

const dropLegendary: PresetEntry = {
  label: 'Item drop legendario',
  description: 'Sparkles dorados sutiles flotando',
  suggestedId: 'fx_legendary_drop_sparkle',
  factory: () =>
    baseDef({
      tags: ['drop', 'overlay'],
      frames: [53, 54, 55, 56],
      emission: { rate: 4, maxAlive: 8, life: [800, 1400] },
      shape: { type: 'circle', radius: 12 },
      velocity: { mode: 'directional', angle: [-100, -80], speed: [8, 16] },
      alphaOverLife: { stops: [[0, 0], [0.3, 1], [1, 0]] },
      sizeOverLife: { stops: [[0, 0.3], [0.5, 0.6], [1, 0.2]] },
      tint: [0xffdd44, 0xffeeaa, 0xffcc22],
    }),
};

// ── Equipped (overlay sobre items equipados) ──────────────────────────────

const equippedFire: PresetEntry = {
  label: 'Equipo fuego',
  description: 'Llamas sobre weapon/armor equipado',
  suggestedId: 'fx_equipped_fire_overlay',
  factory: () =>
    baseDef({
      tags: ['overlay', 'equipped'],
      frames: [10, 11, 12, 13],
      emission: { rate: 12, maxAlive: 18, life: [400, 800] },
      shape: { type: 'rect', w: 16, h: 8 },
      velocity: { mode: 'directional', angle: [-100, -80], speed: [15, 30] },
      alphaOverLife: { stops: [[0, 0], [0.2, 0.8], [1, 0]] },
      sizeOverLife: { stops: [[0, 0.6], [1, 0.2]] },
      tint: [0xff7722, 0xff5511, 0xffaa44],
    }),
};

const equippedElectric: PresetEntry = {
  label: 'Equipo electricidad',
  description: 'Chispas eléctricas saltando del item',
  suggestedId: 'fx_equipped_electric_overlay',
  factory: () =>
    baseDef({
      tags: ['overlay', 'equipped'],
      frames: [46, 47, 70, 71],
      emission: { rate: 6, maxAlive: 8, life: [150, 350] },
      shape: { type: 'rect', w: 12, h: 12 },
      velocity: { mode: 'radial', speed: [40, 80] },
      alphaOverLife: { stops: [[0, 1], [1, 0]] },
      tint: [0x88ccff, 0xffffff, 0xaaddff],
    }),
};

// ── Específicos ───────────────────────────────────────────────────────────

const meditation: PresetEntry = {
  label: 'Meditación',
  description: 'Runas azules rotando lentamente bajo el personaje',
  suggestedId: 'fx_meditate_runes',
  factory: () =>
    baseDef({
      tags: ['aura', 'cast'],
      frames: [62, 63],
      emission: { rate: 3, maxAlive: 6, life: [1500, 2500] },
      shape: { type: 'circle', radius: 18, edgeOnly: true },
      orbit: { radiusX: 22, radiusY: 8, speed: 30 },
      alphaOverLife: { stops: [[0, 0], [0.3, 0.7], [1, 0]] },
      sizeOverLife: { stops: [[0, 0.5], [1, 0.9]] },
      tint: [0x4488ff, 0x66aaff, 0x2266dd],
    }),
};

const levelUp: PresetEntry = {
  label: 'Level up',
  description: 'Explosión dorada vertical, one-shot',
  suggestedId: 'fx_level_up_burst',
  factory: () =>
    baseDef({
      tags: ['impact'],
      frames: [53, 54, 55, 56, 57, 58],
      emission: { rate: 0, burstCount: 40, maxAlive: 40, life: [800, 1400] },
      shape: { type: 'circle', radius: 4 },
      velocity: { mode: 'directional', angle: [-130, -50], speed: [60, 140] },
      physics: { gravityY: 100, drag: 0.4 },
      rotation: { spin: [-180, 180] },
      alphaOverLife: { stops: [[0, 1], [0.7, 1], [1, 0]] },
      sizeOverLife: { stops: [[0, 1], [1, 0.3]] },
      tint: [0xffdd44, 0xffeeaa, 0xffcc22],
      loop: false,
      duration: 1400,
    }),
};

const resurrection: PresetEntry = {
  label: 'Resurrección',
  description: 'Columna de luz blanca/dorada ascendente',
  suggestedId: 'fx_resurrection_pillar',
  factory: () =>
    baseDef({
      tags: ['cast', 'impact'],
      frames: [17, 18, 19],
      emission: { rate: 30, maxAlive: 60, life: [1000, 1800] },
      shape: { type: 'circle', radius: 18 },
      velocity: { mode: 'directional', angle: [-95, -85], speed: [40, 80] },
      alphaOverLife: { stops: [[0, 0], [0.3, 0.9], [1, 0]] },
      sizeOverLife: { stops: [[0, 0.6], [1, 1.2]] },
      tint: [0xffffff, 0xfff7cc, 0xffeeaa],
      loop: false,
      duration: 2200,
    }),
};

// ──────────────────────────────────────────────────────────────────────────
// Catálogo de presets
// ──────────────────────────────────────────────────────────────────────────

export const PARTICLE_PRESETS: PresetEntry[] = [
  // Auras
  auraHealing, auraPoison, auraShield,
  // Proyectiles
  projectileArcane, projectileFire, projectileIce,
  // Impactos
  impactBurst, impactSparks,
  // Drops
  dropLegendary,
  // Equipped
  equippedFire, equippedElectric,
  // Específicos
  meditation, levelUp, resurrection,
];

export function getPresetByLabel(label: string): PresetEntry | undefined {
  return PARTICLE_PRESETS.find((p) => p.label === label);
}
